import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

export default function BackupImagePreviewPanel({
  previewUrl,
  emptyPreviewIcon,
  previewWidth,
  previewHeight,
}) {
  const previewContainerRef = useRef(null);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });

  const normalizedPreviewWidth = Number(previewWidth);
  const normalizedPreviewHeight = Number(previewHeight);

  const hasConfiguredDimensions =
    Number.isFinite(normalizedPreviewWidth) &&
    normalizedPreviewWidth > 0 &&
    Number.isFinite(normalizedPreviewHeight) &&
    normalizedPreviewHeight > 0;

  const updatePreviewFrameSize = useCallback(() => {
    const previewContainer = previewContainerRef.current;
    if (!previewContainer || !hasConfiguredDimensions) {
      setFrameSize({ width: 0, height: 0 });
      return;
    }

    const containerWidth = previewContainer.clientWidth;
    const containerHeight = previewContainer.clientHeight;

    if (containerWidth <= 0 || containerHeight <= 0) {
      setFrameSize({ width: 0, height: 0 });
      return;
    }

    const widthScale = containerWidth / normalizedPreviewWidth;
    const heightScale = containerHeight / normalizedPreviewHeight;
    const scale = Math.min(1, widthScale, heightScale);

    const nextWidth = Math.max(
      1,
      Math.round(normalizedPreviewWidth * scale),
    );
    const nextHeight = Math.max(
      1,
      Math.round(normalizedPreviewHeight * scale),
    );

    setFrameSize((previous) => {
      if (previous.width === nextWidth && previous.height === nextHeight) {
        return previous;
      }
      return { width: nextWidth, height: nextHeight };
    });
  }, [
    hasConfiguredDimensions,
    normalizedPreviewHeight,
    normalizedPreviewWidth,
  ]);

  useEffect(() => {
    updatePreviewFrameSize();
  }, [updatePreviewFrameSize]);

  useEffect(() => {
    const previewContainer = previewContainerRef.current;
    if (!previewContainer || !hasConfiguredDimensions) {
      return undefined;
    }

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => {
        updatePreviewFrameSize();
      });
      resizeObserver.observe(previewContainer);
      return () => {
        resizeObserver.disconnect();
      };
    }

    const handleWindowResize = () => {
      updatePreviewFrameSize();
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [hasConfiguredDimensions, updatePreviewFrameSize]);

  const previewFrameStyle = useMemo(() => {
    if (
      !hasConfiguredDimensions ||
      !frameSize.width ||
      !frameSize.height
    ) {
      return undefined;
    }
    return {
      width: `${frameSize.width}px`,
      height: `${frameSize.height}px`,
    };
  }, [frameSize.height, frameSize.width, hasConfiguredDimensions]);

  const previewFrameClassName = `backup-image-preview-frame${
    hasConfiguredDimensions ? " size-aware" : ""
  }`;
  const previewImageKey = `${previewWidth || 0}x${previewHeight || 0}:${previewUrl || "empty"}`;

  return (
    <div className="backup-image-settings-preview">
      <div className="panel-title">Preview</div>
      <div
        className="backup-image-preview-container"
        ref={previewContainerRef}
      >
        {previewUrl ? (
          <div className={previewFrameClassName} style={previewFrameStyle}>
            <img
              key={previewImageKey}
              src={previewUrl}
              alt="Backup preview"
              className="backup-image-preview-image"
            />
          </div>
        ) : (
          <div className="backup-image-preview-empty">
            <img
              src={emptyPreviewIcon}
              alt="Upload File to Preview"
              className="backup-image-preview-empty-icon"
            />
          </div>
        )}
      </div>
    </div>
  );
}
